<template>
  <v-container fluid class="delivery-list-page pa-0">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />

    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :id="editId"
    />

    <v-card outlined :loading="loading" class="delivery-list-card">
      <div class="delivery-list-header">
        <div class="delivery-title-wrap">
          <v-btn
            v-if="!hideBackBtn"
            icon
            small
            class="mr-2"
            @click="$router.go(-1)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div>
            <div class="page-title">Delivery</div>
            <div class="page-subtitle">
              {{ delivers.length }} delivery{{
                delivers.length === 1 ? "" : "ies"
              }}
              found
            </div>
          </div>
        </div>

        <v-btn small rounded color="primary" dark @click="add">
          <v-icon left small>mdi-plus</v-icon>
          New Delivery
        </v-btn>
      </div>

      <div class="delivery-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          dense
          label="Search delivery"
          single-line
          hide-details
          clearable
          class="delivery-search"
        />

        <v-btn-toggle
          v-if="!$vuetify.breakpoint.smAndDown"
          v-model="deliveryStatus"
          @change="getList()"
          dense
          mandatory
          class="status-toggle"
        >
          <v-btn value="all" small>All</v-btn>
          <v-btn value="pending" small>Pending</v-btn>
          <v-btn value="delivered" small>Delivered</v-btn>
        </v-btn-toggle>

        <v-select
          v-else
          v-model="deliveryStatus"
          :items="statusItems"
          dense
          hide-details
          outlined
          class="status-select"
          @change="getList()"
        />

        <v-spacer />

        <DateTimePicker
          :defaultRange="defaultRange"
          :range="true"
          :hideTime="true"
          v-model="dateTime"
          @submit="getList()"
        />
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">Total Deliveries</div>
            <div class="summary-value">{{ delivers.length }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Pending</div>
            <div class="summary-value">{{ pendingCount }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Delivered</div>
            <div class="summary-value">{{ deliveredCount }}</div>
          </div>
        </div>

        <v-skeleton-loader
          v-if="loading"
          type="list-item-two-line, list-item-two-line, list-item-two-line"
        />

        <div v-else-if="filteredGroupedDelivery.length">
          <div
            v-for="group in filteredGroupedDelivery"
            :key="group.date"
            class="delivery-group"
          >
            <div class="group-header">
              <div>
                <div class="group-title">
                  {{ $moment(group.date).format("dddd, LL") }}
                </div>
                <div class="group-subtitle">
                  {{ group.items.length }} delivery{{
                    group.items.length === 1 ? "" : "ies"
                  }}
                </div>
              </div>
            </div>

            <div
              v-for="item in group.items"
              :key="item.id"
              class="delivery-row"
            >
              <div class="delivery-main">
                <div
                  class="delivery-status-icon"
                  :class="
                    item.status === 'pending'
                      ? 'pending-icon'
                      : 'delivered-icon'
                  "
                >
                  <v-icon small>
                    {{
                      item.status === "pending"
                        ? "mdi-truck-clock-outline"
                        : "mdi-check"
                    }}
                  </v-icon>
                </div>

                <div class="delivery-info">
                  <div class="delivery-title">
                    {{ item.name }}
                  </div>

                  <div class="delivery-meta">
                    Delivery #{{ item.deliveryOrder || "--" }}

                    <template v-if="item.trackingNumber">
                      <span class="dot">•</span>
                      Tracking #{{ item.trackingNumber }}
                    </template>
                  </div>

                  <div class="delivery-time">
                    {{ formatTime(item.startTime) }} -
                    {{ formatTime(item.endTime) }}

                    <span v-if="item.deliveryType">
                      <span class="dot">•</span>
                      {{ item.deliveryType }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="delivery-side">
                <v-chip
                  small
                  class="status-chip"
                  :color="
                    item.status === 'pending'
                      ? 'orange lighten-5'
                      : 'green lighten-5'
                  "
                  :text-color="
                    item.status === 'pending'
                      ? 'orange darken-3'
                      : 'green darken-3'
                  "
                >
                  {{ item.status }}
                </v-chip>

                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn icon small v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item
                      v-if="item.status === 'pending'"
                      @click="changeStatus(item, 'delivered')"
                    >
                      <v-list-item-title>Mark Delivered</v-list-item-title>
                    </v-list-item>

                    <v-list-item v-else @click="changeStatus(item, 'pending')">
                      <v-list-item-title>Mark Pending</v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item @click="editDelivery(item)">
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="deleteDelivery(item)">
                      <v-list-item-title class="red--text"
                        >Delete</v-list-item-title
                      >
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">
          <v-icon size="52" color="grey lighten-1">mdi-truck-outline</v-icon>
          <div class="empty-title">No deliveries found</div>
          <div class="empty-subtitle">
            Try changing the search, status, or date range.
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>
<script>
import deliveryService from "@/modules/Delivery/service.js";

const DateTimePicker = () => import("@/components/DateTimePicker");
const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  name: "DeliveryList",

  props: {
    hideBackBtn: {
      default: false,
    },
    id: {
      default: 0,
    },
    autofocus: {
      default: true,
    },
    color: {
      type: String,
      default: null,
    },
    dashboardMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
  return {
    editId: null,
    createDialog: false,
    editDialog: false,
    search: "",
    deliveryStatus: "all",
    loading: true,
    delivers: [],
    dateTime: {
      start: this.$moment().startOf("day").toISOString(),
      end: this.$moment().add(15, "days").endOf("day").toISOString(),
    },
    defaultRange: "next15Days",
    statusItems: [
      { text: "All", value: "all" },
      { text: "Pending", value: "pending" },
      { text: "Delivered", value: "delivered" },
    ],
  };
},

  components: {
    Create,
    DateTimePicker,
    Edit,
  },

  
  created() {
    this.getList();
  },

  computed: {
    groupedDelivery() {
      const groups = {};

      this.delivers.forEach((item) => {
        const dateKey = this.$moment
          .utc(item.deliveryDate)
          .format("YYYY-MM-DD");

        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }

        groups[dateKey].push(item);
      });

      return Object.keys(groups)
        .map((date) => ({
          date,
          items: groups[date].sort((a, b) => {
            return String(a.startTime || "").localeCompare(
              String(b.startTime || "")
            );
          }),
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    filteredGroupedDelivery() {
      const term = String(this.search || "")
        .toLowerCase()
        .trim();

      if (!term) {
        return this.groupedDelivery;
      }

      return this.groupedDelivery
        .map((group) => {
          return {
            ...group,
            items: group.items.filter((item) => {
              return (
                String(item.name || "")
                  .toLowerCase()
                  .includes(term) ||
                String(item.deliveryOrder || "")
                  .toLowerCase()
                  .includes(term) ||
                String(item.trackingNumber || "")
                  .toLowerCase()
                  .includes(term) ||
                String(item.deliveryType || "")
                  .toLowerCase()
                  .includes(term) ||
                String(item.status || "")
                  .toLowerCase()
                  .includes(term)
              );
            }),
          };
        })
        .filter((group) => group.items.length);
    },

    pendingCount() {
      return this.delivers.filter((row) => row.status === "pending").length;
    },

    deliveredCount() {
      return this.delivers.filter((row) => row.status === "delivered").length;
    },
  },
  methods: {
    changeStatus(item, status) {
      this.$swal({
        title: "Are you sure?",
        text: `Do you want to change delivery status to ${status}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.loading = true;

          deliveryService
            .update(item.id, { status })
            .then(() => {
              this.getList();
            })
            .catch((error) => {
              console.log("changeStatus error", error);
              this.loading = false;
            });
        }
      });
    },

    editDelivery(item) {
      this.editId = item.id;
      this.editDialog = true;
    },

    deleteDelivery(item) {
      this.$swal({
        title: "Are you sure?",
        text: "Do you want to delete this delivery?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.loading = true;

          deliveryService
            .update(item.id)
            .then(() => {
              this.getList();
            })
            .catch((error) => {
              console.log("deleteDelivery error", error);
              this.loading = false;
            });
        }
      });
    },

    formatTime(time) {
      if (!time) {
        return "Not Set";
      }

      return this.$moment(time, "HH:mm").format("hh:mm A");
    },

    add() {
      this.createDialog = true;
    },

    getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      const where = {
        start: this.dateTime.start,
        end: this.dateTime.end,
        status: this.deliveryStatus === "all" ? null : this.deliveryStatus,
      };

      return deliveryService
        .getAll(where)
        .then((response) => {
          this.delivers = response.data || [];
        })
        .catch((error) => {
          console.log("get delivery list error", error);
          this.delivers = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
<style scoped>
.delivery-list-page {
  background: #f8fafc;
}

.delivery-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.delivery-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.delivery-title-wrap {
  display: flex;
  align-items: center;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.delivery-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.delivery-search {
  max-width: 340px;
  min-width: 260px;
}

.status-toggle {
  background: #f8fafc;
  border-radius: 14px;
  padding: 2px;
}

.status-select {
  max-width: 170px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 220px));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.delivery-group {
  margin-bottom: 18px;
}

.group-header {
  background: #f8fafc;
  border-radius: 14px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.group-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.group-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.delivery-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 10px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.delivery-row:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.delivery-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.delivery-status-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pending-icon {
  background: #fff7ed;
  color: #ea580c;
}

.delivered-icon {
  background: #f0fdf4;
  color: #16a34a;
}

.delivery-info {
  min-width: 0;
}

.delivery-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.delivery-meta,
.delivery-time {
  font-size: 12px;
  color: #64748b;
  margin-top: 3px;
}

.delivery-side {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-chip {
  text-transform: capitalize;
  font-weight: 800;
}

.dot {
  margin: 0 6px;
  color: #94a3b8;
}

.empty-state {
  text-align: center;
  padding: 34px 0;
}

.empty-title {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 10px;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

@media (max-width: 600px) {
  .delivery-search {
    max-width: 100%;
    min-width: 100%;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }

  .delivery-row {
    align-items: flex-start;
  }

  .delivery-side {
    flex-direction: column;
    align-items: flex-end;
  }
}
</style>
